import { useState, type FormEvent } from 'react';
import { Navigate } from 'react-router-dom';
import { useAuth } from '@/hooks/useAuth';
import { getUser, isAuthenticated } from '@/lib/auth';

/**
 * First-login interstitial: a user whose account has a one-time temp password
 * (mustChangePassword) is fenced here until they set a real one. On success the
 * useAuth().setPassword() rotates tokens + routes onward.
 */
export function SetPasswordPage() {
  const { setPassword } = useAuth();
  const [newPassword, setNewPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  if (!isAuthenticated()) return <Navigate to="/login" replace />;
  // Reverse-guard: a non-pending user has no business here.
  if (!getUser()?.mustChangePassword) return <Navigate to="/" replace />;

  const tooShort = newPassword.length > 0 && newPassword.length < 8;
  const mismatch = confirm.length > 0 && confirm !== newPassword;
  const canSubmit =
    newPassword.length >= 8 && confirm === newPassword && !submitting;

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    if (!canSubmit) return;
    setError(null);
    setSubmitting(true);
    try {
      await setPassword(newPassword); // rotates tokens + navigates on success
    } catch (err) {
      const msg = (err as { response?: { data?: { message?: string } } })
        ?.response?.data?.message;
      setError(msg ?? 'Could not set password — try a different one.');
      setSubmitting(false);
    }
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 px-4">
      <div className="w-full max-w-sm bg-white rounded-xl border border-gray-200 p-6 shadow-sm">
        <h1 className="text-xl font-bold text-gray-900 mb-1">
          Set a new password
        </h1>
        <p className="text-sm text-gray-600 mb-5">
          Your account was created with a temporary password. Choose a new one to
          continue.
        </p>
        <form
          onSubmit={handleSubmit}
          aria-label="Set password form"
          className="space-y-4"
        >
          <div>
            <label
              htmlFor="new-password"
              className="block text-sm font-medium text-gray-700 mb-1"
            >
              New password
            </label>
            <input
              id="new-password"
              type="password"
              autoComplete="new-password"
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
            />
            {tooShort && (
              <p className="mt-1 text-xs text-red-600">At least 8 characters.</p>
            )}
          </div>
          <div>
            <label
              htmlFor="confirm-password"
              className="block text-sm font-medium text-gray-700 mb-1"
            >
              Confirm password
            </label>
            <input
              id="confirm-password"
              type="password"
              autoComplete="new-password"
              value={confirm}
              onChange={(e) => setConfirm(e.target.value)}
              className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
            />
            {mismatch && (
              <p className="mt-1 text-xs text-red-600">Passwords don&apos;t match.</p>
            )}
          </div>
          {error && (
            <p className="text-sm text-red-600" role="alert">
              {error}
            </p>
          )}
          <button
            type="submit"
            disabled={!canSubmit}
            className="w-full rounded-lg bg-primary-600 px-4 py-2 text-sm font-medium text-white hover:bg-primary-700 disabled:bg-gray-300 disabled:cursor-not-allowed"
          >
            {submitting ? 'Setting…' : 'Set password & continue'}
          </button>
        </form>
      </div>
    </div>
  );
}
